import * as i0 from '@angular/core';
import { EventEmitter, Component, Input, Output, NgModule } from '@angular/core';
import { BcAlertBehavior } from '@bancolombia/design-system-behaviors';
import { BcDialog, BcDialogService } from '@bancolombia/design-system-web/bc-services';
import * as i1 from '@bancolombia/design-system-web/bc-helpers';
import * as i2 from '@bancolombia/design-system-web/bc-button';
import { BcButtonModule } from '@bancolombia/design-system-web/bc-button';
import * as i3 from '@bancolombia/design-system-web/bc-transaction-status';
import { BcTransactionStatusModule } from '@bancolombia/design-system-web/bc-transaction-status';
import * as i4 from '@bancolombia/design-system-web/bc-icon-button';
import { BcIconButtonModule } from '@bancolombia/design-system-web/bc-icon-button';
import * as i5 from '@angular/common';
import { CommonModule } from '@angular/common';
import { BcIconModule } from '@bancolombia/design-system-web/bc-icon';

/**
 * Importa las alertas con los diseños de Bancolombia S.A.
 * Las alertas corresponde a un cuadro de diálogo que disponibiliza
 * información de manera flotante o fija.
 *
 * @export
 * @class BcAlertsComponent
 * @implements {AfterViewInit}
 */
class BcAlertComponent extends BcDialog {
    constructor(helper) {
        super();
        this.helper = helper;
        this.icon = '';
        /**
         * Tipos de iconos
         *
         * @memberof BcAlertsComponent
         */
        this.icons = {
            error: 'ico-error',
            success: 'check-small',
            info: 'lightbulb-small',
            warning: 'hand-five-small',
            inactive: 'megaphone-small',
        };
        /**
         * Indica si el Inline es creado por medio del servicio DialogService, requerido para la emisión de eventos.
         *
         * @type {boolean}
         * @memberof BcInlineAlertComponent
         */
        this.createdByService = false;
        /**
        * Título de la alerta.
        *
        * @memberof BcAlertsComponent
        */
        this.title = '';
        /**
         * Texto de la alerta.
         *
         * @memberof BcAlertsComponent
         */
        this.text = '';
        /**
          * Links en la alerta, solo se pintan los 2 primeros items del arreglo.
          * Esta propiedad se va a deprecar en las proximas versiones de lts
          * @memberof BcAlertsComponent
        */
        this.links = [];
        /**
         * Tipo de alerta [ "error" | "success" | "info" | "warning" | "inactive" ].
         * @type AlertType
         * @memberof BcAlertsComponent
         */
        this.type = 'info';
        /**
         *  /**
         * Tamaño de alerta [ "small" | "align-center"].
         * @type {string}
         * @memberof BcAlertsComponent
         */
        this.size = 'small';
        /**
        *  /**
        * Version lts de alerta [ "Botones" | "Links"].
        * @type {string}
        * @memberof BcAlertsComponent
        */
        this.version = 'Links';
        /**
         * Id del componente.
         *
         * @type {string}
         * @memberof BcAlertsComponent
         */
        this.componentId = this.helper.getId(this);
        /**
         * Indica si la alerta es fija.
         * Si la alert es fija significa que aparecerá como un cuadro de
         * diálogo (popup) en la parte superior de la ventana
         *
         * @type {boolean}
         * @memberof BcAlertsComponent
         */
        this.fixed = true;
        /**
         * Tiempo de cerrado de la alerta en milisegundos.
         * @type {number}
         * @memberof BcAlertsComponent
         */
        this.timeOut = 0;
        /**
         * Alert estatico.
         * Estática significa que aparecerá como un cuadro de
         * diálogo (popup) en la parte superior de la ventana.
         *
         * @type {boolean}
         * @memberof BcAlertsComponent
         */
        this.inline = false;
        /**
         * Si la alerta es estática, indica si se puede descartar o cerrar.
         *
         * @type {boolean}
         * @memberof BcAlertsComponent
         */
        this.dismissible = false;
        /**
         * Se ejecuta cuando la alerta se cierra, aplica solo para alertas inline.
         *
         * @type {EventEmitter<any>}
         * @memberof BcAlertsComponent
         */
        this.onclose = new EventEmitter();
        /**
        * Se ejecuta cuando se le da click a un link.
        *
        * @type {EventEmitter<any>}
        * @memberof BcAlertsComponent
        */
        this.linkClick = new EventEmitter();
        /**
         * Se ejecuta cuando se da click a uno de los botones del alert.
         *
         * @type {EventEmitter<0 | 1>}
         * @memberof BcInlineAlertComponent
         */
        this.onButtonClick = new EventEmitter();
        /**
         * Indica el id del elemento de referencia para insertar la alerta
         *
         * @type {EventEmitter<any>}
         * @memberof BcAlertsComponent
         */
        this.elementRef = '';
        /**
        * Textos de los botones del Inline. Indicar nulo si el Inline no lleva botones.
        *
        * @type {([string] | [string, string])}
        * @memberof BcInlineAlertComponent
        */
        this.buttons = null;
        this.bcAlertBehavior = new BcAlertBehavior();
    }
    ngOnInit() {
        this.fixed = this.inline === true || (this.elementRef && this.elementRef !== '') ? false : true;
    }
    ngAfterViewInit() {
        this.bcAlertBehavior.setId(this.componentId);
        this.bcAlertBehavior.onClose((event) => {
            this.onclose.emit(event);
        });
        this.bcAlertBehavior.setUp();
        setTimeout(() => {
            this.setIconByType();
        });
    }
    /**
     * Validar icono según tipo de alerta
     *
     * @memberof BcAlertsComponent
     */
    setIconByType() {
        this.icon = this.icons[this.type]
            ? this.icons[this.type]
            : this.icons.inactive;
    }
    /**
     * Atributos de la alerta
     *
     * @param {*} inputs
     * @memberof BcAlertsComponent
     */
    onInjectInputs(inputs) {
        this.elementRef = inputs.elementRef;
        this.componentId = inputs?.id || this.componentId;
        this.type = inputs?.type || this.type;
        this.title = inputs?.title || this.title;
        this.text = inputs?.text || this.text;
        this.buttons = inputs?.buttons || this.buttons;
        this.links = inputs?.links || this.links;
        this.timeOut = inputs?.timeout || this.timeOut;
        this.injectInputs(inputs);
        this.closeByTimeout();
        this.createdByService = true;
    }
    /**
    * Inyecta atributos con validaciones adicionales.
    *
    * @param {AlertInputs}
    * @memberof BcAlertsComponent
    */
    injectInputs(inputs) {
        this.size = inputs?.size !== undefined ? inputs.size : this.size;
        this.version = inputs?.version !== undefined ? inputs.version : this.version;
        this.size = inputs?.size !== undefined ? inputs.size : this.size;
        this.positionHigher = inputs?.positionHigher !== undefined ? inputs.positionHigher : this.positionHigher;
    }
    /**
    * Emite el evento cuando se le da click a un link
    *
    * @memberof BcAlertsComponent
    */
    clickEventLink(event) {
        this.linkClick.emit({ text: event.target.innerText, path: event.target.parentElement.href });
    }
    /**
      
     /**
       * Emite la posición del botón al que se le da click dentro del alert.
       *
       * @memberof BcAlertsComponent
       */
    clickButtonEvent(buttonIndex) {
        this.onButtonClick.emit(buttonIndex);
        if (this.createdByService) {
            this.modalInstance.result.next(buttonIndex);
        }
    }
    /**
     * Cerrar alerta
     *
     * @memberof BcAlertsComponent
     */
    closeIt() {
        const setTimeOut = this.inline ? 0 : 300;
        this.bcAlertBehavior.close();
        setTimeout(() => {
            if (!this.inline) {
                this.close('closing');
            }
        }, setTimeOut);
    }
    /**
     * Cancelar alerta
     *
     * @memberof BcAlertsComponent
     */
    cancelIt() {
        this.dismiss('cancelling');
    }
    /**
     * Cerrar alerta según timeout
     *
     * @memberof BcAlertsComponent
     */
    closeByTimeout() {
        if (this.timeOut && this.timeOut !== 0) {
            this.bcAlertBehavior.setTimeOut(this.timeOut);
            setTimeout(() => {
                this.closeIt();
            }, this.timeOut);
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcAlertComponent, deps: [{ token: i1.BcHelperService }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.2.12", type: BcAlertComponent, selector: "bc-alert", inputs: { title: "title", text: "text", links: "links", type: "type", size: "size", version: "version", componentId: "componentId", inline: "inline", dismissible: "dismissible", positionHigher: "positionHigher", buttons: "buttons" }, outputs: { onclose: "onclose", linkClick: "linkClick", onButtonClick: "onButtonClick" }, usesInheritance: true, ngImport: i0, template: "<article class=\"bc-alert-active bc-alert-{{ type }} bc-alert-{{ size }}\" role=\"alert\" [id]=\"componentId\" [ngClass]=\"{\n    'bc-alert-fixed': fixed,\n    'bc-alert-inline': inline,\n    'bc-alert-dismissible': dismissible}\" [attr.positionHigher]=\"positionHigher\">\n  <section class=\"bc-alert-status\"></section>\n  <bc-transaction-status [status]=\"type\" [size]=\"'md'\" [color]=\"type\" [customIcon]=\"icon\"\n    class=\"bc-alert-status-icon\"></bc-transaction-status>\n  <section class=\"bc-alert-body\">\n    <section class=\"bc-alert-content\">\n      <h3 *ngIf=\"title !== ''\" class=\"bc-alert-title\" [innerHTML]=\"title\"></h3>\n      <p class=\"bc-alert-text\" [innerHTML]=\"text\"></p>\n    </section>\n\n    <section *ngIf=\"size !== 'align-center' && version == 'Botones'\" class=\"bc-alert-buttons\">\n      <button *ngIf=\"buttons.length > 0\" bc-button typeButton=\"secondary-inverse\"\n        class=\"bc-button bc-button-small bc-button-hug\" (click)=\"clickButtonEvent(0)\">\n        <span>{{ buttons[0] }}</span>\n      </button>\n      <button *ngIf=\"buttons.length > 1\" bc-button typeButton=\"secondary-inverse\" sizeButton=\"small\"\n        class=\"bc-button bc-button-small bc-button-hug\" (click)=\"clickButtonEvent(1)\">\n        <span>{{ buttons[1] }}</span>\n      </button>\n    </section>\n\n    <section *ngIf=\"size !== 'align-center' && version == 'Links'\" class=\"bc-alert-buttons\">\n      <ul>\n        <li *ngFor=\"let l of links | slice: 0:2\">\n          <a *ngIf=\"l.url\" href=\"{{ l.url }}\" target=\"_blank\" rel=\"noopener noreferrer\"\n            (click)=\"clickEventLink($event)\">\n            <u>{{ l.text }}</u>\n          </a>\n          <a *ngIf=\"!l.url\" target=\"_blank\" rel=\"noopener noreferrer\" (click)=\"clickEventLink($event)\">\n            <u>{{ l.text }}</u>\n          </a>\n        </li>\n      </ul>\n    </section>\n  </section>\n\n  <section class=\"bc-alert-close\">\n    <bc-icon-button [type]=\"'ghost'\" [size]=\"'medium'\" [customIcon]=\"'ico-error'\" [ariaLabel]=\"'Cerrar alerta'\"\n      (click)=\"closeIt()\"></bc-icon-button>\n  </section>\n\n</article>", dependencies: [{ kind: "directive", type: i2.BcButtonDirective, selector: "[bc-button]", inputs: ["typeButton", "sizeButton", "width", "class", "componentId"] }, { kind: "component", type: i3.BcTransactionStatusComponent, selector: "bc-transaction-status", inputs: ["status", "size", "customIcon", "texto", "color", "colorText", "border", "textAriaLabel"] }, { kind: "component", type: i4.BcIconButtonComponent, selector: "bc-icon-button", inputs: ["type", "color", "size", "shadow", "customIcon", "statusButton", "seleted", "selected", "ariaLabel"] }, { kind: "directive", type: i5.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i5.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i5.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "pipe", type: i5.SlicePipe, name: "slice" }] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcAlertComponent, decorators: [{
            type: Component,
            args: [{ selector: 'bc-alert', template: "<article class=\"bc-alert-active bc-alert-{{ type }} bc-alert-{{ size }}\" role=\"alert\" [id]=\"componentId\" [ngClass]=\"{\n    'bc-alert-fixed': fixed,\n    'bc-alert-inline': inline,\n    'bc-alert-dismissible': dismissible}\" [attr.positionHigher]=\"positionHigher\">\n  <section class=\"bc-alert-status\"></section>\n  <bc-transaction-status [status]=\"type\" [size]=\"'md'\" [color]=\"type\" [customIcon]=\"icon\"\n    class=\"bc-alert-status-icon\"></bc-transaction-status>\n  <section class=\"bc-alert-body\">\n    <section class=\"bc-alert-content\">\n      <h3 *ngIf=\"title !== ''\" class=\"bc-alert-title\" [innerHTML]=\"title\"></h3>\n      <p class=\"bc-alert-text\" [innerHTML]=\"text\"></p>\n    </section>\n\n    <section *ngIf=\"size !== 'align-center' && version == 'Botones'\" class=\"bc-alert-buttons\">\n      <button *ngIf=\"buttons.length > 0\" bc-button typeButton=\"secondary-inverse\"\n        class=\"bc-button bc-button-small bc-button-hug\" (click)=\"clickButtonEvent(0)\">\n        <span>{{ buttons[0] }}</span>\n      </button>\n      <button *ngIf=\"buttons.length > 1\" bc-button typeButton=\"secondary-inverse\" sizeButton=\"small\"\n        class=\"bc-button bc-button-small bc-button-hug\" (click)=\"clickButtonEvent(1)\">\n        <span>{{ buttons[1] }}</span>\n      </button>\n    </section>\n\n    <section *ngIf=\"size !== 'align-center' && version == 'Links'\" class=\"bc-alert-buttons\">\n      <ul>\n        <li *ngFor=\"let l of links | slice: 0:2\">\n          <a *ngIf=\"l.url\" href=\"{{ l.url }}\" target=\"_blank\" rel=\"noopener noreferrer\"\n            (click)=\"clickEventLink($event)\">\n            <u>{{ l.text }}</u>\n          </a>\n          <a *ngIf=\"!l.url\" target=\"_blank\" rel=\"noopener noreferrer\" (click)=\"clickEventLink($event)\">\n            <u>{{ l.text }}</u>\n          </a>\n        </li>\n      </ul>\n    </section>\n  </section>\n\n  <section class=\"bc-alert-close\">\n    <bc-icon-button [type]=\"'ghost'\" [size]=\"'medium'\" [customIcon]=\"'ico-error'\" [ariaLabel]=\"'Cerrar alerta'\"\n      (click)=\"closeIt()\"></bc-icon-button>\n  </section>\n\n</article>" }]
        }], ctorParameters: function () { return [{ type: i1.BcHelperService }]; }, propDecorators: { title: [{
                type: Input
            }], text: [{
                type: Input
            }], links: [{
                type: Input
            }], type: [{
                type: Input
            }], size: [{
                type: Input
            }], version: [{
                type: Input
            }], componentId: [{
                type: Input
            }], inline: [{
                type: Input
            }], dismissible: [{
                type: Input
            }], positionHigher: [{
                type: Input
            }], onclose: [{
                type: Output
            }], linkClick: [{
                type: Output
            }], onButtonClick: [{
                type: Output
            }], buttons: [{
                type: Input
            }] } });

/**
 * Importa las alertas con los diseños de Bancolombia S.A.
 * Las alertas corresponde a un cuadro de diálogo que disponibiliza
 * información de manera flotante o fija.
 * @param text Mensaje a mostrar en la alerta
 * @param typeAlert Tipo de alerta
 *
 * @description Alerta para error
 * @example
 * <bc-alert class="bc-alerts-container" text ="Mensaje de error" typeAlert='error'></bc-alert>
 *
 * @description Alerta para éxito
 * @example
 * <bc-alert class="bc-alerts-container" text ="Mensaje de exito" typeAlert='success'></bc-alert>
 *
 * @description Alerta para información
 * @example
 * <bc-alert class="bc-alerts-container" text ="Mensaje de información" typeAlert='info'></bc-alert>
 *
 * @description Alerta para advertencia
 * @example
 * <bc-alert class="bc-alerts-container" text ="Mensaje de advertencia" typeAlert='warning'></bc-alert>
 *
 * @description Alerta para inactividad
 * @example
 * <bc-alert class="bc-alerts-container" text ="Mensaje de informativo" typeAlert='inactive'></bc-alert>
 *
 * @author: Bancolombia S.A
 */
class BcAlertModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcAlertModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: BcAlertModule, declarations: [BcAlertComponent], imports: [BcIconModule, BcButtonModule, BcTransactionStatusModule, BcIconButtonModule, CommonModule], exports: [BcAlertComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcAlertModule, providers: [BcDialogService], imports: [BcIconModule, BcButtonModule, BcTransactionStatusModule, BcIconButtonModule, CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: BcAlertModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [BcAlertComponent],
                    imports: [BcIconModule, BcButtonModule, BcTransactionStatusModule, BcIconButtonModule, CommonModule,],
                    exports: [BcAlertComponent],
                    providers: [BcDialogService]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { BcAlertComponent, BcAlertModule };
